import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Calendar, MoreHorizontal } from "lucide-react";
import news1 from "../../assets/news1.png";
import news2 from "../../assets/news2.png";
import axiosInstanceAuth from "../../apiInstances/axiosInstanceAuth";
import { Helmet } from "react-helmet";
import Layout2 from "../../Layouts/Layout2";
import { ImageSection } from "./ImageSection";
import { TopBar } from "./TopBar";
import { PropertyInstruction } from "./PropertyInstruction";
import { AboutProperty } from "./AboutProperty";
import { PropertyDetails } from "./PropertyDetails";
import { AgentCard } from "./AgentCard";
import { MapAreaBox } from "./MapAreaBox";
import { PropertyEnquiry } from "./PropertyEnquiry";
import { AdvertisementSection } from "./AdvertisementSection";

const DetailedViewProperty = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const [showPICSPopup, setShowPICSPopup] = useState(false);
  const [PropertyData, setPropertyData] = useState({});
  const [PropertyImages, setPropertyImages] = useState([]);
  const [agentData, setAgentData] = useState({});
  const [isPropertyLoading, setIsPropertyLoading] = useState(true);
  const [savedInspectionKeys, setSavedInspectionKeys] = useState(new Set());
  const [savingInspectionKey, setSavingInspectionKey] = useState(null);
  const [savedInspectionIdByKey, setSavedInspectionIdByKey] = useState({});
  const [openMenuKey, setOpenMenuKey] = useState(null);

  useEffect(() => {
    setIsPropertyLoading(true);
    ViewPropertyDatabyID(id);
  }, [id]);

  useEffect(() => {
    const token = localStorage.getItem("Token");
    if (!token || !id) return;
    axiosInstanceAuth
      .get("/savedInspection")
      .then((res) => {
        const list = res?.data?.data ?? [];
        const filtered = (list || []).filter(
          (s) => String(s.property?._id ?? s.property) === String(id)
        );
        const idMap = {};
        const keySet = new Set();
        filtered.forEach((s) => {
          const key = `${String(s.show_date)}-${s.startTime}-${s.endTime}`;
          idMap[key] = s._id;
          keySet.add(key);
        });
        setSavedInspectionKeys(keySet);
        setSavedInspectionIdByKey(idMap);
      })
      .catch(() => { });
  }, [id]);

  const formatInspectionDate = (show_date) => {
    if (!show_date) return "";
    const d = typeof show_date === "string" ? new Date(show_date) : show_date;
    return isNaN(d.getTime()) ? String(show_date) : d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
  };

  const getSlotTimes = (slot) => {
    const pad = (v) => {
      if (v === undefined || v === null) return "";
      const s = String(v);
      return s.length === 1 ? `0${s}` : s;
    };
    const startTime =
      slot.startTime ||
      (slot.start_hr && slot.start_am_pm
        ? `${pad(slot.start_hr)}:${pad(slot.start_min ?? "00")} ${slot.start_am_pm}`
        : undefined);
    const endTime =
      slot.endTime ||
      (slot.end_hr && slot.end_am_pm
        ? `${pad(slot.end_hr)}:${pad(slot.end_min ?? "00")} ${slot.end_am_pm}`
        : undefined);
    return { startTime, endTime };
  };

  const getRelativeLabel = (show_date) => {
    if (!show_date) return "";
    const d = typeof show_date === "string" ? new Date(show_date) : show_date;
    if (isNaN(d.getTime())) return "";
    const today = new Date();
    const startOfDay = (dt) => {
      const x = new Date(dt);
      x.setHours(0, 0, 0, 0);
      return x;
    };
    const diffMs = startOfDay(d).getTime() - startOfDay(today).getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    if (diffDays > 1 && diffDays <= 7 && d.getDay() === 0) return "This Sunday";
    const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    return dayNames[d.getDay()];
  };

  const handleSaveInspectionToProfile = (slot) => {
    const showDate = slot.show_date ?? slot.date;
    const token = localStorage.getItem("Token");
    if (!token) {
      toast.info("Please log in to save inspections to your profile.");
      navigate("/log-in");
      return;
    }
    const { startTime, endTime } = getSlotTimes(slot);
    if (!showDate || !startTime || !endTime) {
      toast.error("Inspection time is not available for this property.");
      return;
    }
    const key = `${String(showDate)}-${startTime}-${endTime}`;
    if (savedInspectionKeys.has(key)) {
      toast.info("Already saved to your profile.");
      return;
    }
    setSavingInspectionKey(key);
    axiosInstanceAuth
      .post("/savedInspection", {
        propertyId: id,
        show_date: showDate,
        startTime,
        endTime,
      })
      .then((res) => {
        if (res?.data?.status) {
          toast.success(res?.data?.message ?? "Saved to profile.");
          setSavedInspectionKeys((prev) => new Set([...prev, key]));
          setSavedInspectionIdByKey((prev) => ({
            ...prev,
            [key]: res?.data?.data?._id ?? prev[key],
          }));
        } else {
          toast.error(res?.data?.message ?? "Could not save.");
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message ?? "Could not save.");
      })
      .finally(() => setSavingInspectionKey(null));
  };

  const handleRemoveInspectionFromProfile = (slot) => {
    const showDate = slot.show_date ?? slot.date;
    const { startTime, endTime } = getSlotTimes(slot);
    const key = `${String(showDate)}-${startTime}-${endTime}`;
    const idToRemove = savedInspectionIdByKey[key];
    if (!idToRemove) {
      toast.error("Unable to find this inspection in your plan.");
      return;
    }
    axiosInstanceAuth
      .delete(`/savedInspection/${idToRemove}`)
      .then((res) => {
        if (res?.data?.status) {
          toast.success(res?.data?.message ?? "Removed from plan.");
          setSavedInspectionKeys((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
          setSavedInspectionIdByKey((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          });
          setOpenMenuKey(null);
        } else {
          toast.error(res?.data?.message ?? "Could not remove.");
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message ?? "Could not remove.");
      });
  };

  const ViewPropertyDatabyID = async (id) => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    try {
      const res = await axiosInstanceAuth.post(`Agency/viewProperty`, {
        id: id,
        userId: userData?.id || null,
      });
      const mydata = res?.data?.data;
      const myagentdata = res?.data?.agentData;
      if (res?.data?.status) {
        setPropertyData({
          ...mydata,
          mydata,
          favourite: localStorage
            .getItem("Saved")
            ?.split(",")
            .includes(mydata?._id),
        });
        setAgentData(myagentdata);

        const frontPageImg = (mydata?.frontPageImg || []).map((obj) => ({
          original: `${obj}`,
        }));
        const propertyImg = (mydata?.propertyImg || []).map((obj) => ({
          original: `${obj}`,
        }));
        const florePlansImg = (mydata?.florePlansImg || []).map((obj) => ({
          original: `${obj}`,
        }));

        setPropertyImages([
          ...frontPageImg,
          ...propertyImg,
          ...florePlansImg,
        ]);
      }
    } catch (err) {
    } finally {
      setIsPropertyLoading(false);
    }
  };

  const shareUrl = window.location.href;

  return (
    <>
      <Helmet>
        {/* <!-- Open Graph / Facebook --> */}
        <meta
          property="og:title"
          content={`${PropertyData?.street_address_number} ${PropertyData?.street_address_name}, ${PropertyData?.suburb}`}
        />
        <meta property="og:description" content={PropertyData?.discription} />
        {/* <meta property="og:image" content="https://www.myrealestate-ng.com/static/media/news2.0720e989b340dffac792.png" /> */}
        {/* <meta property="og:image" content={`https://www.myrealestate-ng.com${news2}`} /> */}
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="website" />

        {/* <!-- Twitter --> */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={shareUrl} />
        <meta
          property="twitter:title"
          content={`${PropertyData?.street_address_number} ${PropertyData?.street_address_name}, ${PropertyData?.suburb}`}
        />
        <meta
          property="twitter:description"
          content={PropertyData?.discription}
        />
      </Helmet>

      <>
        {isPropertyLoading ? (
          <>
            <div className="h-16 bg-gray-200 animate-pulse" />
            <Layout2 className="mt-8 px-5">
              <div className="grid grid-cols-1 xl:grid-cols-4 gap-5 py-5 my-3">
                <div className="xl:col-span-3 space-y-5">
                  <div className="h-28 bg-gray-200 animate-pulse rounded-2xl" />
                  <div className="h-96 bg-gray-200 animate-pulse rounded-2xl" />
                  <div className="h-72 bg-gray-200 animate-pulse rounded-2xl" />
                  <div className="h-64 bg-gray-200 animate-pulse rounded-2xl" />
                </div>
                <div className="xl:col-span-1 sticky top-5">
                  <div className="h-96 bg-gray-200 animate-pulse rounded-2xl" />
                </div>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
                <div className="xl:col-span-3 h-96 bg-gray-200 animate-pulse rounded-2xl" />
                <div className="xl:col-span-1 h-96 bg-gray-200 animate-pulse rounded-2xl" />
              </div>
            </Layout2>
          </>
        ) : (
          <>
            <TopBar PropertyData={PropertyData} />
            <ImageSection
              PropertyData={PropertyData}
              PropertyImages={PropertyImages}
              agentData={agentData}
            />

            <Layout2 className="mt-8 px-5">
              <div className="grid grid-cols-1 xl:grid-cols-4 gap-5 py-5 my-3">
                {/* LEFT CONTENT */}
                <div className="xl:col-span-3 gap-5">
                  <PropertyInstruction PropertyData={PropertyData} />
                  {PropertyData?.inspection_times?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-gray-200 mt-5">
                      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                        <div>
                          <h3 className="text-base sm:text-lg font-semibold text-[#000929]">
                            {PropertyData?.street_address_name || "Inspections"}
                          </h3>
                          <p className="text-xs sm:text-sm text-gray-500">
                            Get notified of changes and cancellations by adding an inspection to your plan.
                          </p>
                        </div>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {PropertyData.inspection_times.map((slot, idx) => {
                          const { startTime, endTime } = getSlotTimes(slot);
                          const key = `${String(slot.show_date)}-${startTime}-${endTime}`;
                          const isSaved = savedInspectionKeys.has(key);
                          const isSaving = savingInspectionKey === key;
                          const label = getRelativeLabel(slot.show_date);
                          return (
                            <div
                              key={idx}
                              className="px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                            >
                              <div>
                                <div className="text-sm font-semibold text-[#000929]">
                                  {label}
                                </div>
                                <div className="text-xs sm:text-sm text-gray-600">
                                  {formatInspectionDate(slot.show_date)}
                                </div>
                                {startTime && endTime && (
                                  <div className="text-xs sm:text-sm text-gray-600">
                                    From {startTime} – {endTime}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-3 self-start sm:self-auto relative">
                                {isSaved && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setOpenMenuKey((prev) =>
                                          prev === key ? null : key
                                        )
                                      }
                                      className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
                                      aria-label="Inspection options"
                                    >
                                      <MoreHorizontal className="w-5 h-5" />
                                    </button>
                                    {openMenuKey === key && (
                                      <div className="absolute right-0 top-8 z-20 w-44 bg-white rounded-xl shadow-lg border border-gray-100 py-1">
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveInspectionFromProfile(slot)}
                                          className="w-full text-left px-4 py-2 text-xs sm:text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                        >
                                          <span>Remove inspection</span>
                                        </button>
                                      </div>
                                    )}
                                  </>
                                )}
                                {isSaved ? (
                                  <span className="inline-flex items-center gap-2 rounded-full bg-[#E6F4FF] text-[#097FE6] text-xs font-semibold px-4 py-3">
                                    <span className="w-2 h-2 rounded-full bg-[#097FE6]" />
                                    In inspection
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    disabled={isSaving}
                                    onClick={() => handleSaveInspectionToProfile(slot)}
                                    className="inline-flex items-center justify-center rounded-full bg-[#097FE6] text-white text-xs font-semibold px-4 py-3 hover:bg-[#0664b7] disabled:opacity-60"
                                  >
                                    {isSaving ? "Adding..." : "Add to inspection"}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <AboutProperty PropertyData={PropertyData} />
                  <PropertyDetails PropertyData={PropertyData} />
                  <MapAreaBox id={id} PropertyData={PropertyData} />
                </div>

                {/* RIGHT SIDEBAR */}
                <div className="xl:col-span-1">
                  <div className="sticky top-5">
                    <AgentCard agentData={agentData} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
                <div className="xl:col-span-3">
                  <PropertyEnquiry agentData={agentData} />
                </div>

                <div className="xl:col-span-1">
                  <AdvertisementSection news1={news1} />
                </div>
              </div>
            </Layout2>
          </>
        )}
      </>

      {/* Personal Information Collection Statement Popup */}
      {showPICSPopup && (
        <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[999999] flex justify-center items-center px-4 sm:px-6">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-4 sm:p-6 relative max-h-[90vh] overflow-y-auto">
              {/* Close Button */}
              <button
                className="absolute top-2 right-3 sm:top-3 sm:right-4 text-2xl sm:text-3xl text-gray-600 hover:text-black"
                onClick={() => setShowPICSPopup(false)}
              >
                &times;
              </button>

              {/* Title */}
              <div className="text-[#171717] font-semibold text-base sm:text-lg mb-3 sm:mb-4 pr-8 break-words">
                Personal Information Collection Statement
              </div>

              {/* Content */}
              <p className="text-[#404040] text-xs sm:text-sm leading-5 sm:leading-6 break-words">
                Your personal information and associated behavioural data
                related to search activities will be passed to the Agency and/or
                its authorised service provider to assist the Agency to contact
                you about your property enquiry. They are required not to use
                your information for any other purpose. Our{" "}
                <Link
                  to={"/privacy-policy"}
                  className="text-blue-600 underline cursor-pointer"
                >
                  Privacy policy
                </Link>{" "}
                explains how we store personal information and how you may
                access, correct or complain about the handling of personal
                information.
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default DetailedViewProperty;
